const ABOUT_SELECTOR = '[data-about-editorial]';
const INFORMATION_ROOT_SELECTOR = '[data-information-root]';
const SUPPORT_NAV_SELECTOR = '[data-support-navigation]';
const DISCLOSURE_SELECTOR = '[data-info-disclosure]';
const DISCLOSURE_SUMMARY_SELECTOR = '[data-info-disclosure-summary]';
const FAQ_ROOT_SELECTOR = '[data-faq-root]';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DISCLOSURE_OPEN_DURATION = 260;
const DISCLOSURE_CLOSE_DURATION = 220;
const DISCLOSURE_OPEN_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const DISCLOSURE_CLOSE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

function findWithin(root, selector) {
  if (!root) return [];

  const matches = root.matches?.(selector) ? [root] : [];
  return [...matches, ...root.querySelectorAll(selector)];
}

function positionHorizontalTarget(scroller, selector) {
  const target = scroller?.querySelector(selector);
  if (!target || scroller.scrollWidth <= scroller.clientWidth) return;

  const scrollerRect = scroller.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const isFullyVisible = targetRect.left >= scrollerRect.left && targetRect.right <= scrollerRect.right;
  if (isFullyVisible) return;

  const delta = targetRect.left + targetRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);
  scroller.scrollBy({ left: delta, behavior: 'auto' });
}

function scheduleHorizontalTarget(scroller, selector) {
  if (!scroller) return;

  if (scroller.currentLinkPositionFrame) {
    window.cancelAnimationFrame(scroller.currentLinkPositionFrame);
  }

  scroller.currentLinkPositionFrame = window.requestAnimationFrame(() => {
    scroller.currentLinkPositionFrame = window.requestAnimationFrame(() => {
      positionHorizontalTarget(scroller, selector);
      scroller.currentLinkPositionFrame = null;
    });
  });
}

function initializeSupportNavigation(scroller) {
  if (!scroller) return;

  if (scroller.supportNavigationReady === true) {
    scheduleHorizontalTarget(scroller, '[aria-current="page"]');
    return;
  }

  scroller.supportNavigationReady = true;
  scheduleHorizontalTarget(scroller, '[aria-current="page"]');

  if (typeof window.ResizeObserver === 'function') {
    scroller.supportNavigationResizeObserver = new window.ResizeObserver(() => {
      scheduleHorizontalTarget(scroller, '[aria-current="page"]');
    });
    scroller.supportNavigationResizeObserver.observe(scroller);
  }
}

function initializeAbout(root) {
  if (!root || root.dataset.aboutEditorialReady === 'true') return;

  const targets = [...root.querySelectorAll('[data-info-reveal]')];
  root.dataset.aboutEditorialReady = 'true';

  if (
    !targets.length
    || typeof window.IntersectionObserver !== 'function'
    || window.matchMedia?.(REDUCED_MOTION_QUERY).matches
  ) {
    targets.forEach((target) => target.classList.add('is-visible'));
    return;
  }

  root.classList.add('is-reveal-ready');

  const observer = new window.IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  targets.forEach((target) => observer.observe(target));
  root.aboutEditorialObserver = observer;
}

function registerManagedListener(store, target, type, handler, options) {
  target.addEventListener(type, handler, options);
  store.push([target, type, handler, options]);
}

function removeManagedListeners(store = []) {
  store.forEach(([target, type, handler, options]) => {
    target.removeEventListener(type, handler, options);
  });
}

function bindPressFeedback(trigger, target, listenerStore) {
  const setPressed = (pressed) => target.classList.toggle('is-pressed', pressed);

  registerManagedListener(listenerStore, trigger, 'pointerdown', () => setPressed(true));
  registerManagedListener(listenerStore, trigger, 'pointerup', () => setPressed(false));
  registerManagedListener(listenerStore, trigger, 'pointerleave', () => setPressed(false));
  registerManagedListener(listenerStore, trigger, 'pointercancel', () => setPressed(false));
  registerManagedListener(listenerStore, trigger, 'blur', () => setPressed(false));
}

function getDisclosureTargetState(disclosure) {
  return typeof disclosure.infoDisclosureTargetOpen === 'boolean'
    ? disclosure.infoDisclosureTargetOpen
    : disclosure.open;
}

function syncDisclosureState(disclosure) {
  const summary = disclosure.querySelector(DISCLOSURE_SUMMARY_SELECTOR);
  const expanded = getDisclosureTargetState(disclosure);

  disclosure.classList.toggle('is-expanded', expanded);
  summary?.setAttribute('aria-expanded', String(expanded));
}

function clearDisclosureStyles(disclosure) {
  disclosure.removeAttribute('data-info-animating');
  disclosure.classList.remove('is-opening', 'is-closing');
  disclosure.style.height = '';
  disclosure.style.overflow = '';
}

function cancelDisclosureAnimation(disclosure) {
  const animation = disclosure.infoDisclosureAnimation;
  if (!animation) return disclosure.getBoundingClientRect().height;

  const currentHeight = disclosure.getBoundingClientRect().height;
  disclosure.infoDisclosureAnimationToken += 1;
  disclosure.infoDisclosureAnimation = null;
  animation.cancel();
  disclosure.style.height = `${currentHeight}px`;
  disclosure.style.overflow = 'hidden';
  return currentHeight;
}

function setDisclosureImmediately(disclosure, shouldOpen) {
  cancelDisclosureAnimation(disclosure);
  disclosure.infoDisclosureTargetOpen = shouldOpen;
  disclosure.open = shouldOpen;
  disclosure.classList.toggle('is-expanded', shouldOpen);
  clearDisclosureStyles(disclosure);
  syncDisclosureState(disclosure);
}

function settleDisclosure(disclosure) {
  setDisclosureImmediately(disclosure, getDisclosureTargetState(disclosure));
}

function getDisclosureBorderHeight(disclosure) {
  const styles = window.getComputedStyle(disclosure);
  return (Number.parseFloat(styles.borderTopWidth) || 0) + (Number.parseFloat(styles.borderBottomWidth) || 0);
}

function animateDisclosure(disclosure, shouldOpen) {
  const summary = disclosure.querySelector(DISCLOSURE_SUMMARY_SELECTOR);
  if (!(summary instanceof HTMLElement)) return;

  const startHeight = cancelDisclosureAnimation(disclosure);
  disclosure.infoDisclosureTargetOpen = shouldOpen;
  disclosure.classList.toggle('is-expanded', shouldOpen);
  disclosure.classList.toggle('is-opening', shouldOpen);
  disclosure.classList.toggle('is-closing', !shouldOpen);
  syncDisclosureState(disclosure);

  const reduceMotion = window.matchMedia?.(REDUCED_MOTION_QUERY).matches;
  if (reduceMotion || typeof disclosure.animate !== 'function') {
    setDisclosureImmediately(disclosure, shouldOpen);
    return;
  }

  disclosure.open = true;
  disclosure.style.height = 'auto';
  disclosure.style.overflow = 'visible';

  const borderHeight = getDisclosureBorderHeight(disclosure);
  const expandedHeight = disclosure.getBoundingClientRect().height;
  const collapsedHeight = summary.getBoundingClientRect().height + borderHeight;
  const endHeight = shouldOpen ? expandedHeight : collapsedHeight;

  disclosure.style.height = `${startHeight}px`;
  disclosure.style.overflow = 'hidden';
  disclosure.setAttribute('data-info-animating', shouldOpen ? 'opening' : 'closing');

  if (Math.abs(endHeight - startHeight) < 1) {
    setDisclosureImmediately(disclosure, shouldOpen);
    return;
  }

  const token = (disclosure.infoDisclosureAnimationToken || 0) + 1;
  disclosure.infoDisclosureAnimationToken = token;
  const animation = disclosure.animate(
    [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
    {
      duration: shouldOpen ? DISCLOSURE_OPEN_DURATION : DISCLOSURE_CLOSE_DURATION,
      easing: shouldOpen ? DISCLOSURE_OPEN_EASING : DISCLOSURE_CLOSE_EASING,
    }
  );

  disclosure.infoDisclosureAnimation = animation;
  animation.onfinish = () => {
    if (disclosure.infoDisclosureAnimationToken !== token) return;

    disclosure.infoDisclosureAnimation = null;
    disclosure.open = shouldOpen;
    clearDisclosureStyles(disclosure);
    syncDisclosureState(disclosure);
  };
}

function decodeHash() {
  if (!window.location.hash) return '';

  try {
    return decodeURIComponent(window.location.hash.slice(1));
  } catch (_error) {
    return window.location.hash.slice(1);
  }
}

function openHashedDisclosure(root = document, shouldScroll = true) {
  const id = decodeHash();
  if (!id) return;

  const target = document.getElementById(id);
  const disclosure = target?.matches?.(DISCLOSURE_SELECTOR)
    ? target
    : target?.closest?.(DISCLOSURE_SELECTOR);

  if (!disclosure || !root.contains(disclosure)) return;

  disclosure.closest(FAQ_ROOT_SELECTOR)?.faqRevealItem?.(disclosure);
  setDisclosureImmediately(disclosure, true);

  if (shouldScroll) {
    window.requestAnimationFrame(() => {
      disclosure.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }
}

function initializeDisclosureRoot(root) {
  if (!root || root.dataset.infoDisclosureReady === 'true') return;

  const listenerStore = [];
  const disclosures = [...root.querySelectorAll(DISCLOSURE_SELECTOR)];
  root.dataset.infoDisclosureReady = 'true';
  root.infoDisclosureListeners = listenerStore;
  root.infoDisclosures = disclosures;

  disclosures.forEach((disclosure) => {
    const summary = disclosure.querySelector(DISCLOSURE_SUMMARY_SELECTOR);
    disclosure.infoDisclosureTargetOpen = disclosure.open;
    disclosure.infoDisclosureAnimationToken = 0;
    syncDisclosureState(disclosure);

    if (!(summary instanceof HTMLElement)) return;

    bindPressFeedback(summary, disclosure, listenerStore);
    registerManagedListener(listenerStore, summary, 'click', (event) => {
      event.preventDefault();
      animateDisclosure(disclosure, !getDisclosureTargetState(disclosure));
    });
    registerManagedListener(listenerStore, disclosure, 'toggle', () => {
      if (disclosure.infoDisclosureAnimation) {
        syncDisclosureState(disclosure);
        return;
      }

      disclosure.infoDisclosureTargetOpen = disclosure.open;
      syncDisclosureState(disclosure);
    });
  });

  openHashedDisclosure(root, false);
}

function initializeFaqRoot(root) {
  if (!root || root.dataset.faqReady === 'true') return;

  const listenerStore = [];
  const searchInput = root.querySelector('[data-faq-search]');
  const categoryRail = root.querySelector('[data-faq-category-rail]');
  const categoryButtons = [...root.querySelectorAll('[data-faq-filter]')];
  const items = [...root.querySelectorAll('[data-faq-item]')];
  const groups = [...root.querySelectorAll('[data-faq-group]')];
  const emptyMessage = root.querySelector('[data-faq-empty]');
  const results = root.querySelector('[data-faq-results]');
  let activeCategory = categoryButtons.find((button) => button.getAttribute('aria-selected') === 'true')?.dataset.faqFilter || 'all';

  root.dataset.faqReady = 'true';
  root.faqListeners = listenerStore;

  const updateCategoryButtons = () => {
    categoryButtons.forEach((button) => {
      const isActive = button.dataset.faqFilter === activeCategory;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });

    const activeButton = categoryButtons.find((button) => button.dataset.faqFilter === activeCategory);
    if (results && activeButton?.id) results.setAttribute('aria-labelledby', activeButton.id);
  };

  const applyFilters = () => {
    const query = searchInput instanceof HTMLInputElement ? searchInput.value.trim().toLocaleLowerCase() : '';
    let visibleCount = 0;

    items.forEach((item) => {
      const category = item.dataset.faqCategory;
      const text = item.textContent.toLocaleLowerCase();
      const matchesCategory = activeCategory === 'all' || category === activeCategory;
      const matchesQuery = query === '' || text.includes(query);
      const isVisible = matchesCategory && matchesQuery;
      item.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });

    groups.forEach((group) => {
      group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
    });

    if (emptyMessage) emptyMessage.hidden = visibleCount !== 0;
  };

  const selectCategory = (button, moveFocus = false) => {
    activeCategory = button.dataset.faqFilter || 'all';
    updateCategoryButtons();
    applyFilters();
    scheduleHorizontalTarget(categoryRail, '[aria-selected="true"]');
    if (moveFocus) button.focus();
  };

  categoryButtons.forEach((button, index) => {
    bindPressFeedback(button, button, listenerStore);
    registerManagedListener(listenerStore, button, 'click', () => selectCategory(button));
    registerManagedListener(listenerStore, button, 'keydown', (event) => {
      let nextIndex;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % categoryButtons.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + categoryButtons.length) % categoryButtons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = categoryButtons.length - 1;
      if (typeof nextIndex !== 'number') return;

      event.preventDefault();
      selectCategory(categoryButtons[nextIndex], true);
    });
  });

  if (searchInput instanceof HTMLInputElement) {
    registerManagedListener(listenerStore, searchInput, 'input', applyFilters);
  }

  root.faqRevealItem = () => {
    activeCategory = 'all';
    if (searchInput instanceof HTMLInputElement) searchInput.value = '';
    updateCategoryButtons();
    applyFilters();
  };

  updateCategoryButtons();
  applyFilters();
}

function findFooterAnchor() {
  const footerSections = document.querySelectorAll('.shopify-section-group-footer-group');

  return document.querySelector('#footer-group')
    || footerSections[footerSections.length - 1]
    || document.querySelector('footer[data-section-id]')
    || document.querySelector('body > footer');
}

function moveInformationAnnouncement() {
  if (
    !document.body.classList.contains('template-information')
    || document.documentElement.classList.contains('shopify-design-mode')
  ) return;

  const announcement = document.querySelector('bottom-announcement-bar.outfitworks-bottom-announcement');
  const footerAnchor = findFooterAnchor();
  if (!announcement || !footerAnchor) return;

  if (footerAnchor.nextElementSibling !== announcement) {
    footerAnchor.insertAdjacentElement('afterend', announcement);
  }

  announcement.classList.add('outfitworks-bottom-announcement--information');
  document.body.classList.add('has-information-flow-announcement');
  observeInformationAnnouncement(announcement);
}

function observeInformationAnnouncement(announcement) {
  if (
    announcement.dataset.infoVisibilityReady === 'true'
    || typeof window.IntersectionObserver !== 'function'
  ) return;

  announcement.dataset.infoVisibilityReady = 'true';
  announcement.infoVisibilityObserver = new window.IntersectionObserver((entries) => {
    const isVisible = entries.some((entry) => entry.isIntersecting && !announcement.hidden);
    document.body.classList.toggle('is-info-announcement-visible', isVisible);
  });
  announcement.infoVisibilityObserver.observe(announcement);
}

let scrollQuietTimer;

function initializeInformationPageScrolling() {
  if (
    !document.body.classList.contains('template-information')
    || document.body.dataset.infoPageScrollReady === 'true'
  ) return;

  document.body.dataset.infoPageScrollReady = 'true';
  window.addEventListener('scroll', () => {
    document.body.classList.add('is-info-page-scrolling');
    window.clearTimeout(scrollQuietTimer);
    scrollQuietTimer = window.setTimeout(() => {
      document.body.classList.remove('is-info-page-scrolling');
    }, 600);
  }, { passive: true });
}

function initializeInformationPages(root = document) {
  findWithin(root, ABOUT_SELECTOR).forEach(initializeAbout);
  findWithin(root, SUPPORT_NAV_SELECTOR).forEach(initializeSupportNavigation);
  findWithin(root, FAQ_ROOT_SELECTOR).forEach(initializeFaqRoot);
  findWithin(root, INFORMATION_ROOT_SELECTOR).forEach(initializeDisclosureRoot);
  moveInformationAnnouncement();
  initializeInformationPageScrolling();
}

initializeInformationPages();

window.addEventListener('hashchange', () => {
  document.querySelectorAll(INFORMATION_ROOT_SELECTOR).forEach((root) => {
    openHashedDisclosure(root);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  initializeInformationPages(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  findWithin(event.target, ABOUT_SELECTOR).forEach((root) => {
    root.aboutEditorialObserver?.disconnect();
    delete root.dataset.aboutEditorialReady;
  });

  findWithin(event.target, INFORMATION_ROOT_SELECTOR).forEach((root) => {
    removeManagedListeners(root.infoDisclosureListeners);
    root.infoDisclosures?.forEach(settleDisclosure);
    delete root.dataset.infoDisclosureReady;
    delete root.infoDisclosureListeners;
    delete root.infoDisclosures;
  });

  findWithin(event.target, FAQ_ROOT_SELECTOR).forEach((root) => {
    removeManagedListeners(root.faqListeners);
    delete root.dataset.faqReady;
    delete root.faqListeners;
    delete root.faqRevealItem;
  });

  findWithin(event.target, SUPPORT_NAV_SELECTOR).forEach((scroller) => {
    scroller.supportNavigationResizeObserver?.disconnect();
    if (scroller.currentLinkPositionFrame) {
      window.cancelAnimationFrame(scroller.currentLinkPositionFrame);
    }
    delete scroller.supportNavigationReady;
    delete scroller.supportNavigationResizeObserver;
    delete scroller.currentLinkPositionFrame;
  });
});
